import { FirebaseApp, initializeApp } from 'firebase/app';
import {
  query,
  where,
  orderBy,
  limit,
  runTransaction,
  getFirestore,
  increment,
  updateDoc,
  addDoc,
  Query,
  DocumentData,
  CollectionReference,
} from 'firebase/firestore';
import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage';
import { Functions, getFunctions, httpsCallable } from 'firebase/functions';
import dayjs from 'dayjs';
import {
  getFirebaseCollectionRef,
  getFirebaseDocument,
  getFirebaseDocuments,
  getFirebaseDocumentRef,
  setFirebaseDocument,
  batchSetFirebaseDocuments,
  deleteFirebaseDocument,
  generateDateTimeString,
  replaceUndefinedWithNull,
  subscribeToFirebaseDocuments,
  WithId,
} from './helpers';

import {
  UserData,
  UserEmailSubscription,
  UserOAuthTokens,
  Project,
  Unit,
  Inspection,
  Team,
  ActionItem,
  ActionItemPayload,
  ActionItemCategory,
  ActionItemStatus,
  email_string,
  TaskWebhook,
  TaskNotificationRequest,
  FeedEvent,
  BuildingWorkflow,
  Process,
  Lease,
  InspectionStatus,
  MessageTemplateType,
  actionTitles,
} from '@urbanmix-tech/shared-js';

import { QueryFieldFilterConstraint } from 'firebase/firestore';

import { v4 as uuidv4 } from 'uuid';

let reportAppError: (error: Error, message?: string) => void;
let app: FirebaseApp;
let functions: Functions;

export type MigrationVersion = {
  id: string;
  version: number;
};

export type COLLECTION_TYPE = 'users' | 'actionItems' | 'units' | 'inspections' | 'teams' | 'projects' |  'taskWebhooks' | 'taskNotifications' | 'feedEvents' | 'userEmailSubscriptions' | 'buildingWorkflows' | 'processes' | 'leases'

export function setReportAppError(
  fn: (error: Error, message?: string) => void
) {
  reportAppError = fn;
}

export type FirebaseConfig = {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId: string;
};

export function initialize(firebaseConfig: FirebaseConfig): void {
  // firebaseConfig = firebaseConfig;
  
  app = initializeApp(firebaseConfig);
  console.log('Firebase app initialized',app);
  functions = getFunctions(app);
}

export function initializeFirebaseApp(firebaseApp: FirebaseApp): void {
  app = firebaseApp;
  functions = getFunctions(app);
}

export function getDatabase() {
  if (!app) {
    throw Error('Firebase not initiated');
  }

  return {
    users: getFirebaseCollectionRef<UserData>('users'),
    user: (id: string) => getFirebaseDocumentRef<UserData>(`users/${id}`),
    teams: getFirebaseCollectionRef<Team>('teams'),
    team: (id: string) => getFirebaseDocumentRef<Team>(`teams/${id}`),
    projects: (isArchive: boolean) =>
      getFirebaseCollectionRef<Project>(
        isArchive ? 'projects_archive' : 'projects'
      ),
    project: (isArchive: boolean, id: string) =>
      getFirebaseDocumentRef<Project>(
        `${isArchive ? 'projects_archive' : 'projects'}/${id}`
      ),
    units: (isArchive: boolean, projectId: string) =>
      getFirebaseCollectionRef<Unit>(
        `${isArchive ? 'projects_archive' : 'projects'}/${projectId}/units`
      ),
    unit: (isArchive: boolean, projectId: string, unitId: string) =>
      getFirebaseDocumentRef<Unit>(
        `${
          isArchive ? 'projects_archive' : 'projects'
        }/${projectId}/units/${unitId}`
      ),
    inspections: (isArchive: boolean, projectId: string, unitId: string) =>
      getFirebaseCollectionRef<Inspection>(
        `${
          isArchive ? 'projects_archive' : 'projects'
        }/${projectId}/units/${unitId}/inspections`
      ),
    inspection: (
      isArchive: boolean,
      projectId: string,
      unitId: string,
      inspectionId: string
    ) =>
      getFirebaseDocumentRef<Inspection>(
        `${
          isArchive ? 'projects_archive' : 'projects'
        }/${projectId}/units/${unitId}/inspections/${inspectionId}`
      ),
    migration: getFirebaseDocumentRef<MigrationVersion>(`settings/migration`),
    actionItems: getFirebaseCollectionRef<ActionItem>('action_items'),
    actionItem: (id: string) =>
      getFirebaseDocumentRef<ActionItem>(`action_items/${id}`),
    actionItemPayload: (id: string) =>
      getFirebaseDocumentRef<ActionItemPayload>(`action_item_payloads/${id}`),
    taskWebhooks: getFirebaseCollectionRef<TaskWebhook>('task_webhooks'),
    taskWebook: (id: string) =>
      getFirebaseDocumentRef<TaskWebhook>(`task_webhooks/${id}`),
    taskNotifications : getFirebaseCollectionRef<TaskNotificationRequest>('task_notification_requests'),
    taskNotification : (id: string) =>
      getFirebaseDocumentRef<TaskNotificationRequest>(`task_notification_requests/${id}`),
    feedEvent: (id: string) => getFirebaseDocumentRef<FeedEvent>(`feed/${id}`),
    feedEvents: getFirebaseCollectionRef<FeedEvent>('feed'),
    userEmailSubscription: (id: string) => getFirebaseDocumentRef<UserEmailSubscription>(`user_email_subscriptions/${id}`),
    userEmailSubscriptions : getFirebaseCollectionRef<UserEmailSubscription>('user_email_subscriptions'),
    buildingWorkflows : getFirebaseCollectionRef<BuildingWorkflow>('building_workflows'),
    processes : getFirebaseCollectionRef<Process>('processes'),
    leases : getFirebaseCollectionRef<Lease>('leases'),
  };
}

export async function getUserDataByIdImp(
  userId: string
): Promise<UserData | null> {
  return getFirebaseDocument(getDatabase().user(userId));
}

export async function saveUserOAuthTokensImp(
  tokensToSave: UserOAuthTokens
): Promise<string | null> {
  const userDocRef = getDatabase().user(tokensToSave.user_id);
  // Get the oauthTokens subcollection
  const oauthTokensColRef = getFirebaseCollectionRef<UserOAuthTokens>(
    userDocRef.path + '/oauthTokens'
  );
  const doc = getFirebaseDocumentRef<UserOAuthTokens>(
    oauthTokensColRef.path + '/' + tokensToSave.id
  );
  return await setFirebaseDocument(doc, tokensToSave);
}

export async function getUserOAuthTokensImp(
  user_id: string
): Promise<UserOAuthTokens[] | null> {
  // Get the user's document
  const userDocRef = getDatabase().user(user_id);
  // Get the oauthTokens subcollection
  const oauthTokensColRef = getFirebaseCollectionRef<UserOAuthTokens>(
    userDocRef.path + '/oauthTokens'
  );
  // Get all documents in the oauthTokens subcollection
  const userTokens = await getFirebaseDocuments(oauthTokensColRef);

  return userTokens;
}

export async function saveUserImp(
  userToSave: UserData
): Promise<string | null> {
  return setFirebaseDocument(getDatabase().user(userToSave.id), userToSave);
}

export async function getAllUsersImp(): Promise<UserData[] | null> {
  return getFirebaseDocuments(query(getDatabase().users));
}

export async function getTeamByNameImp(name: string): Promise<Team | null> {
  return getFirebaseDocument(getDatabase().team(name));
}

export async function getAllTeamsImp(limits?: number): Promise<Team[] | null> {
  if (limits === undefined) {
    return getFirebaseDocuments(query(getDatabase().teams));
  }

  return getFirebaseDocuments(
    query(getDatabase().teams, orderBy('name'), limit(limits))
  );
}

export async function saveTeamImp(teamToSave: Team): Promise<string | null> {
  return setFirebaseDocument(getDatabase().team(teamToSave.name), teamToSave);
}

export async function deleteTeamImp(name: string): Promise<boolean> {
  return deleteFirebaseDocument(getDatabase().team(name));
}

export async function getActionItemPayloadImp(
  id: string
): Promise<ActionItemPayload | null> {
  return getFirebaseDocument(getDatabase().actionItemPayload(id));
}

export async function saveActionItemPayloadImp(
  actionItemPayloadToSave: ActionItemPayload
): Promise<string | null> {
  return setFirebaseDocument(
    getDatabase().actionItemPayload(actionItemPayloadToSave.id),
    actionItemPayloadToSave
  );
}

export async function getActionItemImp(id: string): Promise<ActionItem | null> {
  return getFirebaseDocument(getDatabase().actionItem(id));
}

export async function findConsiderNewInspectionActionImp(
  projectId: string,
  buildingId: number,
  unitId: string,
  updateDate: Date
): Promise<ActionItem[]> {
  return getFirebaseDocuments(
    query(
      getDatabase().actionItems,
      where('projectId', '==', projectId),
      where('buildingId', '==', buildingId),
      where('unitId', '==', unitId),
      where('title', '==', actionTitles.considerNewInspeciton),
      where('updateDate', '>=', updateDate),
      orderBy('updateDate', 'desc'),
      orderBy('createDate', 'desc')
    )
  );
}

export async function findActionItemsByUnitIdAndTitleImp(
  projectId: string,
  buildingId: number,
  unitId: string,
  title: string
): Promise<ActionItem[]> {
  return getFirebaseDocuments(
    query(
      getDatabase().actionItems,
      where('projectId', '==', projectId),
      where('buildingId', '==', buildingId),
      where('unitId', '==', unitId),
      where('title', '==', title),
      orderBy('updateDate', 'desc'),
      orderBy('createDate', 'desc')
    )
  );
}

export async function findActionItemsByCategoryAndStatusImp(
  projectId: string,
  buildingId: number,
  unitId: string,
  category: ActionItemCategory,
  status: ActionItemStatus[]
): Promise<ActionItem[]> {
  return getFirebaseDocuments(
    query(
      getDatabase().actionItems,
      where('projectId', '==', projectId),
      where('buildingId', '==', buildingId),
      where('unitId', '==', unitId),
      where('category', '==', category),
      where('status', 'in', status),
      orderBy('updateDate', 'desc'),
      orderBy('createDate', 'desc')
    )
  );
}

export async function findActionItemsByUnitIdAndCategoryImp(
  projectId: string,
  buildingId: number,
  unitId: string,
  category: ActionItemCategory
): Promise<ActionItem[]> {
  return getFirebaseDocuments(
    query(
      getDatabase().actionItems,
      where('projectId', '==', projectId),
      where('buildingId', '==', buildingId),
      where('unitId', '==', unitId),
      where('category', '==', category),
      orderBy('updateDate', 'desc'),
      orderBy('createDate', 'desc')
    )
  );
}

export async function findActionItemsByFiltersImp(
  projectId?: string,
  buildingId?: number,
  unitId?: string,
  category?: ActionItemCategory,
  dueDate?: Date,
  owner?: email_string,
  supervisor?: email_string,
  follower?: email_string
): Promise<ActionItem[]> {
  let filters: Array<QueryFieldFilterConstraint> = [];

  if (owner) {
    filters.push(where('owner.email', '==', owner));
  }
  if (dueDate) {
    filters.push(where('dueDate', '==', dueDate));
  }
  if (supervisor) {
    filters.push(where('supervisor.email', '==', supervisor));
  }
  if (follower) {
    filters.push(where('followers', 'array-contains', follower));
  }
  if (category) {
    filters.push(where('category', '==', category));
  }
  if (unitId) {
    filters.push(where('unitId', '==', unitId));
  }
  if (buildingId) {
    filters.push(where('buildingId', '==', buildingId));
  }
  if (projectId) {
    filters.push(where('projectId', '==', projectId));
  }
  return getFirebaseDocuments(
    query(
      getDatabase().actionItems,
      ...filters,
      orderBy('updateDate', 'desc'),
      orderBy('createDate', 'desc')
    )
  );
}

export async function findCheckInOnInspectionProgressActionsImp(
  projectId: string,
  buildingId: number,
  unitId: string
): Promise<ActionItem[]> {
  return getFirebaseDocuments(
    query(
      getDatabase().actionItems,
      where('projectId', '==', projectId),
      where('buildingId', '==', buildingId),
      where('unitId', '==', unitId),
      where('title', '==', actionTitles.checkInOnInspectionProgress),
      orderBy('updateDate', 'desc'),
      orderBy('createDate', 'desc')
    )
  );
}

export async function saveActionItemImp(
  actionItemToSave: ActionItem
): Promise<string | null> {  
  return setFirebaseDocument(
    getDatabase().actionItem(actionItemToSave.id),
    actionItemToSave
  );
}

export async function deleteActionItemImp(id: string): Promise<boolean> {
  return deleteFirebaseDocument(getDatabase().actionItem(id));
}

export async function getActionsByUnitIdImp(
  projectId: string,
  buildingId: number,
  unitId: string
): Promise<ActionItem[]> {
  return getFirebaseDocuments(
    query(
      getDatabase().actionItems,
      where('projectId', '==', projectId),
      where('buildingId', '==', buildingId),
      where('unitId', '==', unitId),
      orderBy('updateDate', 'desc'),
      orderBy('createDate', 'desc')
    )
  );
}

export async function getActionsByProjectIdImp(
  projectId: string
): Promise<ActionItem[]> {
  return getFirebaseDocuments(
    query(
      getDatabase().actionItems,
      where('projectId', '==', projectId),
      orderBy('updateDate', 'desc'),
      orderBy('createDate', 'desc')
    )
  );
}

export async function subscribeActionsByProjectIdImp(
  projectId: string,
  onUpdate: (actionItems: ActionItem[]) => void,
  onError: (error: Error) => void
): Promise<() => void> {
  const actionItemsQuery = query(
    getDatabase().actionItems,
    where('projectId', '==', projectId),
    orderBy('updateDate', 'desc'),
    orderBy('createDate', 'desc')
  )

  return subscribeToFirebaseDocuments(actionItemsQuery, onUpdate, onError);
}

export async function subscribeActionsByUnitIdImp(
  projectId: string,
  buildingId: number,
  unitId: string,
  onUpdate: (actionItems: ActionItem[]) => void,
  onError: (error: Error) => void
): Promise<() => void> {
  const actionItemsQuery = query(
    getDatabase().actionItems,
    where('projectId', '==', projectId),
    where('buildingId', '==', buildingId),
    where('unitId', '==', unitId),
    orderBy('updateDate', 'desc'),
    orderBy('createDate', 'desc')
  )

  return subscribeToFirebaseDocuments(actionItemsQuery, onUpdate, onError);
}

export async function getActionsImp(): Promise<ActionItem[]> {
  return getFirebaseDocuments(
    query(
      getDatabase().actionItems,
      orderBy('updateDate', 'desc'),
      orderBy('createDate', 'desc')
    )
  );
}

export async function getInspectionByIdImp(
  projectId: string,
  unitId: string,
  inspectionId: string,
  archive: boolean = false
): Promise<Inspection | null> {
  return getFirebaseDocument(
    getDatabase().inspection(archive, projectId, unitId, inspectionId)
  );
}

export async function findInspectionsByStatusImp(
  projectId: string,
  unitId: string,
  status: InspectionStatus,
  archive: boolean = false
): Promise<Inspection[]> {
  return getFirebaseDocuments(
    query(
      getDatabase().inspections(archive, projectId, unitId),
      where('status', '==', status),
      orderBy('updateDate', 'desc'),
      orderBy('createDate', 'desc')
    )
  );
}

export async function saveInspectionImp(
  projectId: string,
  unitId: string,
  inspectionToSave: Inspection,
  archive: boolean = false
): Promise<string | null> {
  return setFirebaseDocument(
    getDatabase().inspection(archive, projectId, unitId, inspectionToSave.id),
    inspectionToSave
  );
}

export async function saveInspectionsImp(
  projectId: string,
  unitId: string,
  inspectionToSaves: Inspection[],
  archive: boolean = false
): Promise<boolean> {
  return batchSetFirebaseDocuments(
    inspectionToSaves.map(inspection => ({
      docRef: getDatabase().inspection(
        archive,
        projectId,
        unitId,
        inspection.id
      ),
      data: inspection,
    }))
  );
}

export async function deleteInspectionImp(
  projectId: string,
  unitId: string,
  inspectionId: string,
  archive: boolean = false
): Promise<boolean> {
  return deleteFirebaseDocument(
    getDatabase().inspection(archive, projectId, unitId, inspectionId)
  );
}

export async function getInspectionsByUnitIdImp(
  projectId: string,
  unitId: string,
  archive: boolean = false
): Promise<Inspection[]> {
  return getFirebaseDocuments(
    query(
      getDatabase().inspections(archive, projectId, unitId),
      orderBy('updateDate', 'desc'),
      orderBy('createDate', 'desc')
    )
  );
}

export async function deleteInspectionsByUnitIdImp(
  projectId: string,
  unitId: string,
  archive: boolean = false
): Promise<boolean> {
  const inspections = await getInspectionsByUnitIdImp(projectId, unitId);

  if (archive) {
    await saveInspectionsImp(projectId, unitId, inspections, true);
  }

  const promises = inspections.map(async inspection => {
    return deleteInspectionImp(projectId, unitId, inspection.id);
  });

  const results = await Promise.allSettled(promises);

  const failed = results.filter(
    promise => promise.status === 'rejected' || promise.value === false
  );

  if (failed.length > 0) {
    return false;
  }

  return true;
}

export async function getProjectByIdImp(
  projectId: string,
  archive: boolean = false
): Promise<Project | null> {
  return getFirebaseDocument(getDatabase().project(archive, projectId));
}

export async function saveProjectImp(
  projectToSave: Project,
  archive: boolean = false
): Promise<string | null> {
  return setFirebaseDocument(
    getDatabase().project(archive, projectToSave.id),
    projectToSave
  );
}

export async function getUnitByIdImp(
  projectId: string,
  unitId: string,
  archive: boolean = false
): Promise<Unit | null> {
  return getFirebaseDocument(getDatabase().unit(archive, projectId, unitId));
}

export async function getUnitsByProjectIdImp(
  projectId: string,
  archive: boolean = false
): Promise<Unit[]> {
  return getFirebaseDocuments(query(getDatabase().units(archive, projectId)));
}

export async function getUnitsByBuildingIdImp(
  projectId: string,
  buildingId: number,
  archive: boolean = false
): Promise<Unit[]> {
  return getFirebaseDocuments(
    query(
      getDatabase().units(archive, projectId),
      where('buildingId', '==', buildingId)
    )
  );
}

export function updateUnitImp(
  projectId: string,
  unitId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  update: Record<string, any>,
  archive: boolean = false
): Promise<void> {
  const cleanData = replaceUndefinedWithNull(update);

  const unitRef = getDatabase().unit(archive, projectId, unitId);

  return updateDoc(unitRef, cleanData);
}

export async function saveUnitImp(
  projectId: string,
  unitToSave: Unit,
  archive: boolean = false
): Promise<string | null> {
  const project = await getProjectByIdImp(projectId);

  if (!project) {
    return null;
  }

  const oldUnit = await getUnitByIdImp(projectId, unitToSave.id);

  if (oldUnit) {
    const updateData: Record<string, unknown> = { ...unitToSave };

    delete updateData.lastInspectionId;

    await updateUnitImp(projectId, unitToSave.id, updateData);
  } else {
    const newUnitId = await setFirebaseDocument(
      getDatabase().unit(archive, projectId, unitToSave.id),
      unitToSave
    );

    if (newUnitId === null) {
      return null;
    }
  }

  return unitToSave.id;
}

export async function saveUnitsImp(
  projectId: string,
  units: Unit[],
  archive: boolean = false
): Promise<boolean> {
  return batchSetFirebaseDocuments(
    units.map(unit => ({
      docRef: getDatabase().unit(archive, projectId, unit.id),
      data: unit,
    }))
  );
}

export async function deleteUnitImp(
  projectId: string,
  unitId: string,
  archive: boolean = false
): Promise<boolean> {
  const project = await getProjectByIdImp(projectId);

  if (!project) {
    return false;
  }

  const oldUnit = await getUnitByIdImp(projectId, unitId);

  if (!oldUnit) {
    return true;
  }

  const completed = await deleteInspectionsByUnitIdImp(
    projectId,
    unitId,
    archive
  );

  if (!completed) {
    return false;
  }

  return deleteFirebaseDocument(getDatabase().unit(false, projectId, unitId));
}

export async function deleteUnitsByProjectIdImp(
  projectId: string,
  archive?: boolean
): Promise<boolean> {
  const units = await getUnitsByProjectIdImp(projectId);

  if (archive) {
    await saveUnitsImp(projectId, units, true);
  }

  const promises = units.map(async unit => {
    return deleteUnitImp(projectId, unit.id, archive);
  });

  const results = await Promise.allSettled(promises);

  const failed = results.filter(
    promise => promise.status === 'rejected' || promise.value === false
  );

  if (failed.length > 0) {
    return false;
  }

  return true;
}

export async function getProjectsImp(
  archive: boolean = false
): Promise<Project[]> {
  return getFirebaseDocuments(query(getDatabase().projects(archive)));
}

export async function deleteProjectImp(
  projectId: string,
  archive: boolean = false
): Promise<boolean> {
  if (archive) {
    const project = await getProjectByIdImp(projectId);

    if (!project) {
      return false;
    }

    await saveProjectImp(project, true);
  }

  const completed = await deleteUnitsByProjectIdImp(projectId, archive);

  if (!completed) {
    return false;
  }

  return deleteFirebaseDocument(getDatabase().project(false, projectId));
}

export async function getMigrationVersionImp(): Promise<MigrationVersion | null> {
  return getFirebaseDocument(getDatabase().migration);
}

export async function saveMigrationVersionImp(
  migrationVersionToSave: MigrationVersion
): Promise<string | null> {
  return setFirebaseDocument(getDatabase().migration, migrationVersionToSave);
}

export async function uploadGlbFileImp(
  file: File,
  projectId: string,
  buildingId: string
): Promise<string | null> {
  try {
    const storage = getStorage();
    const storageRef = ref(
      storage,
      `projects/${projectId}/${buildingId}/model/${generateDateTimeString()}_${
        file.name
      }`
    );

    await uploadBytes(storageRef, file);

    return await getDownloadURL(storageRef);
  } catch (err) {
    reportAppError(err as Error);
  }

  return null;
}

export async function uploadUnitAttachmentImp(
  file: File,
  filename: string,
  projectId: string,
  buildingId: string
): Promise<string | null> {
  try {
    const storage = getStorage();
    const storageRef = ref(
      storage,
      `projects/${projectId}/${buildingId}/files/${generateDateTimeString()}_${filename}`
    );

    await uploadBytes(storageRef, file);

    return await storageRef.toString();
  } catch (err) {
    reportAppError(err as Error);
  }

  return null;
}

export async function uploadActionItemAttachmentImp(
  file: File,
  filename: string,
  projectId: string,
  buildingId: number,
  taskId: string
): Promise<string | null> {
  try {
    const storage = getStorage();
    const storageRef = ref(
      storage,
      `projects/${projectId}/${buildingId}/tasks/${taskId}/${generateDateTimeString()}_${filename}`
    );

    await uploadBytes(storageRef, file);

    return storageRef.toString();
  } catch (err) {
    reportAppError(err as Error);
  }

  return null;
}

export async function getActionItemsImp(): Promise<ActionItem[]> {
  return getFirebaseDocuments(query(getDatabase().actionItems));
}

export async function getActionItemsByBuildingIdImp(
  projectId: string,
  buildingId: number
): Promise<ActionItem[]> {
  return getFirebaseDocuments(
    query(
      getDatabase().actionItems,
      where('projectId', '==', projectId),
      where('buildingId', '==', buildingId),
      orderBy('unitId', 'asc'),
      orderBy('updateDate', 'desc'),
      orderBy('createDate', 'desc')
    )
  );
}

export async function getActionItemsByUnitIdImp(
  projectId: string,
  buildingId: number,
  unitId: string
): Promise<ActionItem[]> {
  console.log('shared-js getting action for unit', projectId, buildingId, unitId);

  const action_items_collection = getDatabase().actionItems
  return getFirebaseDocuments(
    query(
      action_items_collection,
      where('projectId', '==', projectId),
      where('buildingId', '==', +buildingId),
      where('unitId', '==', unitId),
      orderBy('updateDate', 'desc'),
      orderBy('createDate', 'desc')
    )
  );
}

export async function createNewInspectionImp(
  projectId: string,
  unitId: string,
  inspectionToSave: Inspection
): Promise<string | null> {
  const unitRef = getDatabase().unit(false, projectId, unitId);
  try {
    const newInspectionId = await runTransaction(
      getFirestore(),
      async transaction => {
        const unitSnap = await transaction.get(unitRef);
        const unitData = unitSnap.data();
        const lastInspectionId = unitData?.lastInspectionId || 0;
        const nextInspectionId = `INS_${lastInspectionId + 1}`;
        const inspectionData = {
          ...inspectionToSave,
          id: nextInspectionId,
        };
        const inspectionRef = getDatabase().inspection(
          false,
          projectId,
          unitId,
          nextInspectionId
        );

        transaction.set(inspectionRef, inspectionData);
        transaction.update(unitRef, {
          lastInspectionId: increment(1),
        });

        return nextInspectionId;
      }
    );
    return newInspectionId;
  } catch (err) {
    reportAppError(err as Error);
  }
  return null;
}

export async function sendMessageImp({
  to,
  projectName,
  unitName,
  inspectorName,
  assignerName,
  scheduledDate,
  address,
  leaseEndDate,
  templateType,
}: {
  to: string;
  projectName: string;
  unitName: string;
  inspectorName: string;
  assignerName: string;
  scheduledDate: Date;
  address: string;
  leaseEndDate: Date | null;
  templateType: MessageTemplateType;
}) {
  return httpsCallable(
    functions,
    'sendMessage'
  )({
    to,
    projectName,
    unitName,
    inspectorName,
    assignerName,
    scheduledDateString: dayjs(scheduledDate).format('MM/DD/YYYY'),
    address,
    leaseEndDateString: leaseEndDate
      ? dayjs(leaseEndDate).format('MM/DD/YYYY')
      : '',
    templateType,
  }).then(data => data.data as { success: boolean });
}

export async function saveWebhookImp(
  webhookId: string, url : string, secret_token : string, eventPayload: string  
): Promise<string | null> {
  
  const id = uuidv4();
  return await setFirebaseDocument(getDatabase().taskWebook(id), {
    id, webhookId, payload: eventPayload,secret_token,url,
    timestamp: new Date()
  });
}

export async function getWebhookImp(
  id : string
): Promise<TaskWebhook | null> {
  return await getFirebaseDocument(getDatabase().taskWebook(id));
}

export async function getWebhooksImp(): Promise<TaskWebhook[] | null> {
  return await getFirebaseDocuments(query(getDatabase().taskWebhooks));
}


export async function deleteWebhookImp(id: string) : Promise<void> {
  
  const webhooks = await getFirebaseDocuments(
    query(
      getDatabase().taskWebhooks,
      where('webhookId','==',id)
    ))
  
  webhooks.forEach(async (element : TaskWebhook) => {
    await deleteFirebaseDocument(getDatabase().taskWebook(element.id));
  });
}

export async function createTaskNotificationRequestImp(
  request : Omit<TaskNotificationRequest,'id'>
): Promise<string | null> {
  const id = uuidv4();
  return await setFirebaseDocument(getDatabase().taskNotification(id), request);
}

export async function getTaskNotificationRequestImp(
  id : string
): Promise<TaskNotificationRequest | null> {
  return await getFirebaseDocument(getDatabase().taskNotification(id));
}

export async function getTaskNotificationRequestByTaskIdImp(
  taskId : string
): Promise<TaskNotificationRequest[]> {
  
  return await getFirebaseDocuments(query(getDatabase().taskNotifications, where('taskId','==',taskId)));
}

export async function deleteTaskNotificationRequestImp(id: string) : Promise<void> {
  await deleteFirebaseDocument(getDatabase().taskNotification(id));
}

// Feed Events
export async function getFeedEventImp(
  id: string
): Promise<FeedEvent | null> {
  return await getFirebaseDocument(getDatabase().feedEvent(id));
}

export async function getFeedEventsImp(): Promise<FeedEvent[] | null> {
  return await getFirebaseDocuments(query(getDatabase().feedEvents));
}

export async function saveFeedEventImp(
  feedEvent: FeedEvent
): Promise<string | null> {
  return await setFirebaseDocument(getDatabase().feedEvent(feedEvent.id), feedEvent);
}

export async function deleteFeedEventImp(id: string): Promise<void> {
  await deleteFirebaseDocument(getDatabase().feedEvent(id));
}

export function basicFiltersImp(
  projectId?: string,
  buildingId?: number, 
  unitId?: string,
  userId?: string,
  constraints? : QueryFieldFilterConstraint[]
): QueryFieldFilterConstraint[] {

  let filters: Array<QueryFieldFilterConstraint> = [];
  
  if (projectId) {
    filters.push(where('projectId', '==', projectId));
  }

  if (buildingId) {
    filters.push(where('buildingId', '==', buildingId));
  }

  if (unitId) {
    filters.push(where('unitId', '==', unitId));
  }

  if (userId) {
    filters.push(where('userId', '==', userId));
  }

  if (constraints) {
    filters = filters.concat(constraints);
  }

  return filters;   
}

export function feedEventsQueryImp(
  projectId?: string,
  buildingId?: number, 
  unitId?: string,
  userId?: string,
  ...constraints: QueryFieldFilterConstraint[]
): Query<WithId<FeedEvent>, DocumentData> {
  return query(
      getDatabase().feedEvents,
      ...basicFiltersImp(projectId, buildingId, unitId, userId, constraints),      
    );  
}

export function genericQueryImp<T>(collection : CollectionReference<T>, filters: {projectId?: string,buildingId?:number,unitId?:string,userId?:string},constraints? : QueryFieldFilterConstraint[]): Query<T, DocumentData> {
  const combinedConstraints = basicFiltersImp(filters.projectId, filters.buildingId,filters.unitId,filters.userId,constraints);
  return query(
    collection,
    ...combinedConstraints
  );
}

export async function getItemsImp<T>(query : Query<WithId<T>,DocumentData>): Promise<T[]> {
  return getFirebaseDocuments(query);
}

export async function subscribeToImp<T>(query : Query<WithId<T>,DocumentData>, onUpdate: (items: T[]) => void, onError: (error: Error) => void): Promise<() => void> {
  return subscribeToFirebaseDocuments(query, onUpdate, onError);
}

export async function saveUserEmailSubscriptionImp(
  subscription: UserEmailSubscription
): Promise<string | null> {
  return setFirebaseDocument(getDatabase().userEmailSubscription(subscription.id) , subscription);
}

export async function getUserEmailSubscriptionsImp(
  userId: string
): Promise<UserEmailSubscription[] | null> {
  return getFirebaseDocuments(query(getDatabase().userEmailSubscriptions,where('user_id','==',userId)));
}

export async function getUserEmailSubscriptionByIdImp(
  id: string
): Promise<UserEmailSubscription | null> {
  return getFirebaseDocument(getDatabase().userEmailSubscription(id));
}

export async function getUserEmailSubscriptionByHistoryIdImp(
  historyId: string
): Promise<UserEmailSubscription | null> {
  return getFirebaseDocuments(query(getDatabase().userEmailSubscriptions,where('historyId','==',historyId))).then((data) => {
    // return the first document
    if (data.length > 0) {
      return data[0];
    } else {
      return null;
    }
  });  
}

export async function deleteUserEmailSubscriptionImp(
  userId?: string,
  service?: string,
  id?: string
): Promise<void> {
  if (id) {
    deleteFirebaseDocument(getDatabase().userEmailSubscription(id));
  } else {
    const constraints : QueryFieldFilterConstraint[] = [];
    if (userId) {
      constraints.push(where('user_id','==',userId));
    }
    if (service) {
      constraints.push(where('service','==',service));
    }
    return getFirebaseDocuments(query(getDatabase().userEmailSubscriptions,...constraints)).then((data) => {
      data.forEach((subscription) => {
        deleteFirebaseDocument(getDatabase().userEmailSubscription(subscription.id));
      })
    });    
  }  
}

/**
 * A workflow is scoped to a project either by the legacy single `projectId`
 * field or by the `projectIds` array (which lets one document serve several
 * projects instead of being duplicated per project). Both are still read; a
 * document listing the project in both places is returned once.
 */
export async function getBuildingWorkflowsImp(
  projectId: string,
): Promise<BuildingWorkflow[]> {
  const [byProjectId, byProjectIds] = await Promise.all([
    getFirebaseDocuments(query(getDatabase().buildingWorkflows,where('projectId','==',projectId))),
    getFirebaseDocuments(query(getDatabase().buildingWorkflows,where('projectIds','array-contains',projectId))),
  ]);

  const workflowsById = new Map<string, BuildingWorkflow>();
  [...byProjectId, ...byProjectIds].forEach((workflow) => {
    workflowsById.set(workflow.id, workflow);
  });

  // Firestore returns unordered queries in document id order, so sorting the
  // merged set the same way keeps the button order stable - and identical to
  // before for projects whose workflows all still use `projectId`. Compare by
  // byte value rather than localeCompare, which collates ids like `Ab1`/`aB1`
  // differently to Firestore.
  return [...workflowsById.values()].sort((a, b) =>
    a.id < b.id ? -1 : a.id > b.id ? 1 : 0
  );
}

export async function saveGenericObjectmImp<T>(type: COLLECTION_TYPE, object: WithId<T>): Promise<string | null> {  
  const collectionRef = getCollectionRef(type);
  let docRef = getFirebaseDocumentRef<T>(`${collectionRef.path}/${object.id}`)
  return setFirebaseDocument(
    docRef,
    object    
  );
}

export async function getGenericObjectImp<T>(type: COLLECTION_TYPE, id: string): Promise<T | null> {
  
  const collectionRef = getCollectionRef(type);
  let docRef = getFirebaseDocumentRef<T>(`${collectionRef.path}/${id}`)
  return getFirebaseDocument(docRef);
}

export function getCollectionRef<T>(type: COLLECTION_TYPE) : CollectionReference<T> {
  return getDatabase()[type] as CollectionReference<T>;
}
export async function deleteGenericObjImp<T>(type: COLLECTION_TYPE, id: string): Promise<boolean> {

  const collectionRef = getCollectionRef(type);
  let docRef = getFirebaseDocumentRef<T>(`${collectionRef.path}/${id}`)
  return deleteFirebaseDocument(docRef);
}

// Lease-specific implementations
export async function getLeasesByUnitIdImp(
  projectId: string,
  unitId: string
): Promise<Lease[]> {
  if (!projectId || !unitId) {
    throw new Error('projectId and unitId are required');
  }

  return getFirebaseDocuments(
    query(
      getDatabase().leases,
      where('projectId', '==', projectId),
      where('unitId', '==', unitId),
      orderBy('leaseStartDate', 'desc')
    )
  );
}

export async function getLeasesImp(
  filter?: {
    projectId?: string;
    buildingId?: number;
    unitId?: string;
    status?: 'valid' | 'canceled' | 'draft' | 'proposed';
    leaseStartAfter?: string;
    leaseStartBefore?: string;
    leaseEndAfter?: string;
    leaseEndBefore?: string;
    source?: string;
  },
  onUpdate?: (items: Lease[]) => void
): Promise<Lease[] | (() => void)> {
  const {
    projectId,
    buildingId,
    unitId,
    status,
    leaseStartAfter,
    leaseStartBefore,
    leaseEndAfter,
    leaseEndBefore,
    source,
  } = filter || {};

  const filters: QueryFieldFilterConstraint[] = [];

  if (projectId) {
    filters.push(where('projectId', '==', projectId));
  }

  if (buildingId !== undefined) {
    filters.push(where('buildingId', '==', buildingId));
  }

  if (unitId) {
    filters.push(where('unitId', '==', unitId));
  }

  if (status) {
    filters.push(where('status', '==', status));
  }

  if (source) {
    filters.push(where('source', '==', source));
  }

  if (leaseStartAfter) {
    filters.push(where('leaseStartDate', '>=', leaseStartAfter));
  }

  if (leaseStartBefore) {
    filters.push(where('leaseStartDate', '<=', leaseStartBefore));
  }

  if (leaseEndAfter) {
    filters.push(where('leaseEndDate', '>=', leaseEndAfter));
  }

  if (leaseEndBefore) {
    filters.push(where('leaseEndDate', '<=', leaseEndBefore));
  }

  const leaseQuery = query(getDatabase().leases, ...filters, orderBy('leaseStartDate', 'desc'));

  if (onUpdate) {
    return subscribeToImp(
      leaseQuery,
      onUpdate,
      (error) => {
        console.error('error getting lease documents', error);
      }
    );
  }

  return getFirebaseDocuments(leaseQuery);
}

export async function getCurrentLeaseImp(
  projectId: string,
  unitId: string
): Promise<Lease | null> {
  if (!projectId || !unitId) {
    throw new Error('projectId and unitId are required');
  }

  const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format

  const leases = await getFirebaseDocuments(
    query(
      getDatabase().leases,
      where('projectId', '==', projectId),
      where('unitId', '==', unitId),
      where('status', '==', 'valid'),
      where('leaseStartDate', '<=', today),
      where('leaseEndDate', '>=', today),
      limit(1)
    )
  );

  return leases.length > 0 ? leases[0] : null;
}
